import {
  type StateMachineConfig,
  type BaseState,
  type BaseEvent,
  type BaseContext,
  type BaseEventLookup,
  type Transition,
  type Machine,
  type TransitionListener,
  type StateConfig,
  type EventProducer,
  type EventListener,
  type StateCommand,
} from './state-machine-types';

/**
 * Create an instance of a state machine.
 * @param config State machine configuration with candidate states and transitions.
 * @param triggerChange An optional callback indicating whether a change in context requires a state transition.
 * @returns An instance of a state machine.
 */
export function createMachine<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext
>(
  config: StateMachineConfig<State, Event, EventLookup, Context>
): Machine<State, Event, EventLookup, Context> {
  type StateNode = StateConfig<State, Event, EventLookup, Context>;
  type StateTree = Partial<Record<State, StateNode>>;
  type StatePathNode = {
    state: State;
    node: StateNode;
  };

  const pathLookup = new Map<State | StateCommand, StatePathNode[]>();
  const currentConfig: StateMachineConfig<State, Event, EventLookup, Context> = config;

  const transitionListeners: TransitionListener<Event, EventLookup, Context, State>[] =
    [];
  const eventListeners: EventProducer<Event>[] = [];
  const doneListeners: VoidFunction[] = [];

  const prepare = (
    stateTree: StateTree,
    statePrefix = '',
    statePath: StatePathNode[] = []
  ) => {
    for (const [state, branch] of Object.entries(stateTree)) {
      const path = `${statePrefix}${state}`;
      const nextTree = branch as StateNode;
      const nextPath = [...statePath, { state, node: branch }] as StatePathNode[];

      pathLookup.set(state as State, nextPath);

      if (nextTree.states) {
        prepare(nextTree.states, `${path}:`, nextPath);
      }
    }
  };

  // Pre-warm all state node paths.
  prepare(config.states);

  const getNode = (
    statePath: StatePathNode[],
    position = statePath.length
  ): StateNode => {
    return statePath[position - 1].node;
  };

  const findState = (state: State): StatePathNode[] => {
    return pathLookup.get(state) ?? (console.log(pathLookup, config, state), []);
  };

  const findTransition = (
    statePath: StatePathNode[],
    event: EventLookup[Event['type']]
  ): Transition<State, Context, EventLookup, Event['type']> | undefined => {
    for (let idx = statePath.length - 1; idx >= 0; idx--) {
      const node = getNode(statePath, idx + 1);
      if ('transitions' in node) {
        const transitions = node.transitions[event.type as Event['type']] ?? [];
        const transition = transitions.find((transition) => {
          const transitionGuards = transition.guard.every((guard) =>
            guard(currentContext, event)
          );
          if (!transitionGuards) {
            return;
          }

          if (transitionGuards && transition.state === 'done') {
            return transition;
          }

          const statePath = pathLookup.get(transition.state);
          if (!statePath) {
            return;
          }

          const stateGuards = getNode(statePath)?.guards;
          if (!stateGuards) {
            return transitionGuards ? transition : undefined;
          }

          if (stateGuards.every((g) => g())) {
            return transition;
          }
        });

        if (transition) {
          return transition;
        }

        if (node.trap) {
          break;
        }
      }
    }
  };
  const sendEvent = (event: Event) => {
    eventListeners.forEach((listener) => listener(event));
  };

  let prevStatePath: StatePathNode[] = findState(currentConfig.initial);
  let currentStatePath: StatePathNode[] = findState(currentConfig.initial);

  const initialNode = {
    state: currentConfig.initial,
    node: currentStatePath[currentStatePath.length - 1].node,
  };

  let prevContext: Context = { ...currentConfig.context };
  let currentContext: Context = { ...currentConfig.context };

  const send = (event: EventLookup[Event['type']]) => {
    const state = getNode(currentStatePath);

    if ('passthroughs' in state) {
      // When in a passthrough state, we do not perform explicit transitions.
      // Such transitions occur once, on entry. If not satisfied, the machine
      // will remain in the passthrough state.
      return;
    }

    const transition = findTransition(currentStatePath, event);
    if (transition) {
      doTransition(event, transition);
      doPassthrough(event);
    }
  };

  const doTransition = <T extends Event['type']>(
    event: EventLookup[T],
    transition: Transition<State, Context, EventLookup, T>
  ) => {
    prevStatePath = [...currentStatePath];

    // If we have reached a final state, we tell the consumer of the state machine,
    // and reset it to its initial state.
    if (transition.state === 'done') {
      resolveContext<T>(transition, event);
      getNode(currentStatePath).effects.onExit?.(
        currentContext,
        currentContext,
        sendEvent
      );

      doneListeners.forEach((listener) => listener());
      return;
    }

    currentStatePath = findState(transition.state);

    prevContext = { ...currentContext };
    // Once a transition has been found, update the current context in preparation
    // for executing any effects for this state transition.
    resolveContext<T>(transition, event);
    transitionListeners.forEach((listener) => {
      listener(
        event,
        currentContext,
        {
          previous: previous(),
          current: state(),
        },
        transition
      );
    });

    resolveEffects();
  };

  const doPassthrough = <T extends Event['type']>(event: EventLookup[T]) => {
    const nextState = getNode(currentStatePath);
    if ('passthroughs' in nextState) {
      const passthrough = nextState.passthroughs.find((passthrough) =>
        passthrough.guard.every((guard) => guard(currentContext, event))
      );

      if (!passthrough) {
        return;
      }

      doTransition(event, passthrough);
    }
  };

  const previous = (): State => prevStatePath[prevStatePath.length - 1].state;
  const current = (): State => currentStatePath[currentStatePath.length - 1].state;

  const state = (): State => current();
  const context = (): Context => currentContext;

  const resolveContext = <T extends Event['type']>(
    transition: Transition<State, Context, EventLookup, T>,
    event: EventLookup[T]
  ) => {
    transition.context.forEach((contextUpdater) => {
      currentContext = contextUpdater(currentContext, event);
    });
  };

  const resolveEffects = () => {
    let prev = 0;
    let curr = 0;
    let overlapUntil = 0;

    while (prev < prevStatePath.length && curr < currentStatePath.length) {
      if (prevStatePath[prev].state === currentStatePath[curr].state) {
        overlapUntil++;
      }
      prev++;
      curr++;
    }

    for (prev = overlapUntil; prev < prevStatePath.length; prev++) {
      getNode(prevStatePath, prev + 1).effects.onExit?.(
        prevContext,
        currentContext,
        sendEvent
      );
    }
    for (curr = overlapUntil; curr < currentStatePath.length; curr++) {
      getNode(currentStatePath, curr + 1).effects.onEnter?.(currentContext, sendEvent);
    }

    if (previous() === current()) {
      getNode(currentStatePath).effects.onReEnter?.(
        prevContext,
        currentContext,
        sendEvent
      );
    }
  };

  const execute = () => {
    getNode(currentStatePath).effects.onExecute?.(currentContext, sendEvent);
  };

  const from = (initial: State, context?: Context) => {
    const nextConfig: StateMachineConfig<State, Event, EventLookup, Context> = {
      ...config,
      initial,
    };

    if (context) {
      nextConfig.context = context;
    }

    const machine = createMachine(nextConfig);

    transitionListeners.forEach((transitionListener) => {
      machine.onTransition(transitionListener);
    });
    doneListeners.forEach((doneListener) => {
      machine.onDone(doneListener);
    });

    return machine;
  };

  const reset = () => {
    prevStatePath = [{ ...initialNode }];
    currentStatePath = [{ ...initialNode }];

    prevContext = { ...currentConfig.context };
    currentContext = { ...currentConfig.context };
  };

  const destroy = () => {
    prevStatePath.length = 0;
    currentStatePath.length = 0;

    pathLookup.clear();
  };

  const onTransition = (
    listener: TransitionListener<Event, EventLookup, Context, State>
  ) => {
    transitionListeners.push(listener);
  };

  const onEvent = (listener: EventListener<Event>) => {
    eventListeners.push(listener);
  };

  const onDone = (listener: VoidFunction) => {
    doneListeners.push(listener);
  };

  return {
    send,
    state,
    context,
    execute,
    reset,
    destroy,
    onTransition,
    onEvent,
    onDone,
    from,
    config: () => config,
  };
}
